
import type { FileSystem, Prompt, UserData, AppSettings } from '../types';
import { INITIAL_DATA } from '../constants';
import { requestJson, ERROR_MESSAGES, ApiError } from './api';

const DEFAULT_SETTINGS: AppSettings = {
    globalSystemInstruction: '',
    promptGeneratorSystemPrompt: '',
    promptOptimizerSystemPrompt: ''
};

interface CachedUserData {
    email: string;
    data: UserData;
}

// Früher lag die Bibliothek zusätzlich unter user_cache. Mit gesyncten Skills (mehrere MB) sprengt die
// doppelte Ablage das localStorage-Limit; dann scheitert ein Schreibvorgang und der veraltete Stand bleibt
// liegen. Gelesen wird der alte Schlüssel nur noch als Rückfall, beim Speichern wird er entfernt.
const LEGACY_CACHE_KEY = 'user_cache';
const getDataKey = (email: string) => `prompt_manager_data_${email}`;

const loadLegacyCache = (email: string): UserData | null => {
    try {
        const raw = localStorage.getItem(LEGACY_CACHE_KEY);
        const cached: CachedUserData | null = raw ? JSON.parse(raw) : null;
        return cached && cached.email === email ? cached.data : null;
    } catch (e) {
        console.error('Failed to read user cache:', e);
        return null;
    }
};

const clearLegacyCache = (): void => {
    try {
        localStorage.removeItem(LEGACY_CACHE_KEY);
    } catch (e) {
        console.error('Failed to clear user cache:', e);
    }
};

export const loadUserData = (email: string): UserData => {
    try {
        const rawData = localStorage.getItem(getDataKey(email));
        if (rawData) {
            const data = JSON.parse(rawData);
            if (data.fileSystem && Array.isArray(data.templates)) {
                return data;
            }
        }
    } catch (e) {
        console.error("Failed to load or parse user data:", e);
    }
    const legacy = loadLegacyCache(email);
    if (legacy) return legacy;
    return {
        fileSystem: INITIAL_DATA,
        templates: [],
        settings: DEFAULT_SETTINGS,
    };
};

export const saveUserData = (email: string, fileSystem: FileSystem, templates: Prompt[], settings?: AppSettings): void => {
    clearLegacyCache();
    try {
        const data: UserData = { 
            fileSystem, 
            templates, 
            settings: settings || DEFAULT_SETTINGS,
            updatedAt: new Date().toISOString()
        };
        const serialized = JSON.stringify(data);
        try {
            localStorage.setItem(getDataKey(email), serialized);
        } catch {
            // Speicher voll: alten Stand entfernen und einmal neu versuchen, damit kein veralteter Stand liegen bleibt
            localStorage.removeItem(getDataKey(email));
            localStorage.setItem(getDataKey(email), serialized);
        }
    } catch (e) {
        console.error("Failed to save user data:", e);
    }
};

export const deleteUserData = (email: string): void => {
    try {
        localStorage.removeItem(getDataKey(email));
        clearLegacyCache();
    } catch (e) {
        console.error("Failed to delete user data:", e);
    }
};

export const fetchUserData = async (userId: number): Promise<UserData | null> => {
    try {
        return await requestJson<UserData>(`/api/users/${userId}/data`);
    } catch {
        return null;
    }
};

/** Nur der Änderungszeitpunkt – billig genug, um ihn regelmäßig abzufragen. */
export const fetchUserDataVersion = async (userId: number): Promise<{ updatedAt?: string } | null> => {
    try {
        return await requestJson<{ updatedAt?: string }>(`/api/users/${userId}/data/version`);
    } catch {
        return null;
    }
};

export const saveUserDataToServer = async (
    userId: number,
    data: UserData,
    setStatus?: (status: 'idle' | 'saving' | 'offline' | 'unauthorized' | 'error') => void
): Promise<{ success: boolean; updatedAt?: string; conflict?: boolean }> => {
    try {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            setStatus?.('offline');
            return { success: false };
        }
        setStatus?.('saving');
        const res = await requestJson<{ success: boolean; updatedAt?: string }>(`/api/users/${userId}/data`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        }, ERROR_MESSAGES.request);
        setStatus?.('idle');
        return { success: true, updatedAt: res?.updatedAt };
    } catch (e) {
        console.error('Failed to save user data to server:', e);
        if (e instanceof ApiError && e.status === 409) {
            // Server wurde zwischenzeitlich geändert (anderer Tab, Skill-Sync); der Aufrufer führt zusammen
            setStatus?.('idle');
            return { success: false, conflict: true };
        } else if (e instanceof ApiError && e.status === 401) {
            setStatus?.('unauthorized');
        } else if (e instanceof ApiError && e.status && e.status >= 400) {
            setStatus?.('error');
        } else {
            setStatus?.('offline');
        }
        return { success: false };
    }
};
